import type { AxiosRequestConfig } from "axios"

import { defineRequest, defineRequestWithParam, instance } from "../axios"
import type {
    AudienceCheckNameResponse,
    AudienceCompareResponse,
    AudienceCopyResponse,
    AudienceCreateResponse,
    AudienceExportResponse,
    AudienceInfoResponse,
    AudienceListResponse,
    AudienceStatusListResponse,
    AudienceTypeListResponse,
    AudienceUpdateResponse,
    CheckAudienceNamePayload,
    CreateAudiencePayload,
    EditAudienceInfoPayload,
    FilterConditionChoicesResponse,
    FilterConditionsResponse,
} from "./types"

// 取得受眾列表
export const getAudienceList = defineRequest<AudienceListResponse>({
    url: "/audiences/",
    method: "get",
})

// 創建新受眾
export const createAudience = defineRequest<AudienceCreateResponse, CreateAudiencePayload>({
    url: "/audiences/",
    method: "post",
})

// 複製受眾
export const duplicateAudience = defineRequestWithParam<AudienceCopyResponse>({
    url: (audienceId: string) => `/audiences/${audienceId}/copy/`,
    method: "post",
})

// 取得受眾資訊
export const getAudienceInfo = defineRequestWithParam<AudienceInfoResponse>({
    url: (audienceId: string) => `/audiences/${audienceId}/`,
    method: "get",
})

// 修改受眾資訊
export const editAudienceInfo = defineRequestWithParam<AudienceUpdateResponse, EditAudienceInfoPayload>({
    url: (audienceId: string) => `/audiences/${audienceId}/`,
    method: "put",
})

// 停用此受眾
export const disableAudience = defineRequestWithParam<void>({
    url: (audienceId: string) => `/audiences/${audienceId}/disable/`,
    method: "post",
})

// 刪除此受眾
export const deleteAudience = defineRequestWithParam<void>({
    url: (audienceId: string) => `/audiences/${audienceId}/`,
    method: "delete",
})

// 取得條件列表
export const getFilterConditions = defineRequest<FilterConditionsResponse>({
    url: "/audiences/filter-conditions/",
    method: "get",
})

// 取得條件列表子條件選項列表 目前僅有 dataType： List 、 String 會使用到這隻 API
export const getFilterConditionsChoices = defineRequestWithParam<FilterConditionChoicesResponse>({
    url: (audienceId: string) => `/audiences/filter-conditions/${audienceId}/choices/`,
    method: "get",
})

// 將受眾條件送出分析獲得 Job id
export const audiencesAnalyze = defineRequest<void>({
    url: "/audiences/analyze/",
    method: "post",
})

// 將受眾條件送出分析
export const audiencesFetch = defineRequestWithParam<void>({
    url: (uuid: string) => `/audiences/${uuid}/fetch/`,
    method: "get",
})

// 受眾分析出來的 GID 表格
export const getAudiencesMember = defineRequestWithParam<void>({
    url: (uuid: string) => `/audiences/${uuid}/members/`,
    method: "get",
})

// 下載名單
export const exportAudiences = defineRequestWithParam<AudienceExportResponse>({
    url: (uuid: string) => `/audiences/${uuid}/export/`,
    method: "post",
})

// 取得受眾分析比較結果
// 這裡的 timeout: 0 要蓋掉呼叫端可能傳入的 timeout，defineRequest 的 { ...descriptor, ...config } 順序
// 會讓呼叫端的 config 蓋掉 descriptor，跟現在這個需求相反，所以維持手寫
export function compareAudiences(config: AxiosRequestConfig = {}) {
    return instance.request<AudienceCompareResponse>({
        url: `/audiences/compare/`,
        method: "post",
        ...config,
        timeout: 0,
    })
}

// 確認受眾名稱是否重複
export const checkAudienceName = defineRequest<AudienceCheckNameResponse, CheckAudienceNamePayload>({
    url: "/audiences/name-check/",
    method: "post",
})

// 取消受眾計算
export const cancelAudienceCalculate = defineRequestWithParam<void>({
    url: (uuid: string) => `/audiences/${uuid}/cancel-calculation/`,
    method: "post",
})

// 受眾再分群
export const splitAudience = defineRequestWithParam<void>({
    url: (uuid: string) => `/audiences/${uuid}/split/`,
    method: "post",
})

// 取得受眾類型
export const getAudienceType = defineRequest<AudienceTypeListResponse>({
    url: "/audiences/type/",
    method: "get",
})

// 取得受眾狀態
export const getAudienceStatus = defineRequest<AudienceStatusListResponse>({
    url: "/audiences/status/",
    method: "get",
})
